import { useCallback, useRef, useState } from 'react';
import type { GeoBoundingBox, SearchVideosResult, VideoListItem, VideoStatus } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';

/** Critères de recherche pilotés par l'IHM (sous-ensemble de `searchVideosSchema`, hors emprise). */
export interface SearchFilters {
  q?: string;
  status?: VideoStatus;
  assetId?: string;
  pilotName?: string;
  /** Dates locales « AAAA-MM-JJ » issues des champs `<input type="date">`. */
  flownFrom?: string;
  flownTo?: string;
  hasAnnotations?: boolean;
  labelIds?: string[];
  /** Emprise géographique [ouest, sud, est, nord] (FR-21), dessinée sur le canevas souverain. */
  bbox?: GeoBoundingBox;
  sortBy: 'createdAt' | 'flownAt' | 'title' | 'duration';
  sortOrder: 'asc' | 'desc';
}

const PAGE_SIZE = 24;

/** Corps de requête : on n'envoie que les critères renseignés (un champ vide ne filtre pas). */
function buildBody(filters: SearchFilters, cursor?: string): Record<string, unknown> {
  return {
    limit: PAGE_SIZE,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    ...(cursor ? { cursor } : {}),
    ...(filters.q?.trim() ? { q: filters.q.trim() } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.assetId ? { assetId: filters.assetId } : {}),
    ...(filters.pilotName?.trim() ? { pilotName: filters.pilotName.trim() } : {}),
    ...(filters.flownFrom ? { flownFrom: new Date(filters.flownFrom).toISOString() } : {}),
    // Borne de fin incluse : jusqu'à la fin de la journée choisie, pas son minuit de début.
    ...(filters.flownTo
      ? { flownTo: new Date(`${filters.flownTo}T23:59:59.999`).toISOString() }
      : {}),
    ...(filters.hasAnnotations !== undefined ? { hasAnnotations: filters.hasAnnotations } : {}),
    ...(filters.labelIds?.length ? { labelIds: filters.labelIds } : {}),
    ...(filters.bbox ? { bbox: filters.bbox } : {}),
  };
}

/**
 * Recherche avancée de vidéos (FR-19→22) avec pagination par curseur. `search` (re)lance une
 * recherche depuis la première page ; `loadMore` ajoute la page suivante aux résultats déjà affichés,
 * en rejouant exactement les mêmes critères — mémorisés — pour que le curseur reste cohérent.
 */
export function useVideoSearch() {
  const [items, setItems] = useState<VideoListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const lastFilters = useRef<SearchFilters | null>(null);

  const run = useCallback(async (filters: SearchFilters, cursor?: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<SearchVideosResult>('/videos/search', {
        method: 'POST',
        body: buildBody(filters, cursor),
      });
      setItems((prev) => (cursor ? [...prev, ...res.items] : res.items));
      setNextCursor(res.nextCursor);
      lastFilters.current = filters;
      setSearched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'La recherche a échoué.');
    } finally {
      setLoading(false);
    }
  }, []);

  const search = useCallback((filters: SearchFilters) => void run(filters), [run]);
  const loadMore = useCallback(() => {
    if (nextCursor && lastFilters.current) void run(lastFilters.current, nextCursor);
  }, [nextCursor, run]);

  return { items, nextCursor, loading, error, searched, search, loadMore };
}
